// src/components/opportunity/OpportunityPricingFormGeneric.tsx

import { useMemo } from "react";
import type { OpportunityAmountMode, OpportunityPricingTotals } from "@/types/opportunity";
import { computePricingTotals } from "@/lib/opportunity/calc";
import { evaluateFormatPreset, subtotalBeforeSalesAdjustments } from "@/lib/opportunity/formatPresetPricing";
import { evaluateSalesAdjustments } from "@/lib/opportunity/salesAdjustmentsEval";
import type { FormatCalcLine } from "@/lib/opportunity/formatCalcLineTypes";
import type { FormatPresetPricingContext } from "@/lib/opportunity/formatPresetPricing";
import {
  pricingInputsFromForm,
  type OpportunityPricingFormState,
} from "@/components/opportunity/OpportunityPricingForm";
import { MoneyField, PctField, pctDelta } from "@/components/opportunity/OpportunityPricingFields";
import { PricingCostEntrySection } from "@/components/opportunity/PricingCostEntrySection";
import { OpportunitySalesAdjustmentsSection } from "@/components/opportunity/OpportunitySalesAdjustmentsSection";
import { OpportunityPricingLiveTotals } from "@/components/opportunity/OpportunityPricingLiveTotals";
import type { FormatDepositRuleDef } from "@/lib/opportunity/formatDepositTypes";
import { formatOpportunityCurrency } from "@/lib/opportunity/formatCurrency";

export type OpportunityPricingFormGenericProps = {
  form: OpportunityPricingFormState;
  setForm: (next: OpportunityPricingFormState) => void;
  calcLines: FormatCalcLine[];
  formatPreset: FormatPresetPricingContext;
  presetLoading: boolean;
  presetError: string | null;
  renderSavePanel: () => React.ReactNode;
};

export function OpportunityPricingFormGeneric(props: OpportunityPricingFormGenericProps) {
  const { form, setForm, calcLines, formatPreset, presetLoading, presetError, renderSavePanel } =
    props;

  const inputs = useMemo(() => pricingInputsFromForm(form), [form]);
  const formatEval = useMemo(
    () => evaluateFormatPreset({ inputs, preset: formatPreset }),
    [inputs, formatPreset],
  );
  const totals: OpportunityPricingTotals = useMemo(
    () => computePricingTotals(inputs, { formatPreset }),
    [inputs, formatPreset],
  );
  const preAdj = useMemo(
    () => subtotalBeforeSalesAdjustments({ inputs, preset: formatPreset }),
    [inputs, formatPreset],
  );
  const salesAdjEval = useMemo(
    () => evaluateSalesAdjustments(preAdj.amount, inputs.sales_adjustments),
    [preAdj.amount, inputs.sales_adjustments],
  );

  function update<K extends keyof OpportunityPricingFormState>(
    key: K,
    value: OpportunityPricingFormState[K],
  ) {
    setForm({ ...form, [key]: value });
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-3 min-w-0">
          {presetError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              Could not load proposal format preset: {presetError}
            </div>
          ) : null}

          {presetLoading ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">Loading format preset…</p>
          ) : (
            <PricingCostEntrySection
              baseAmountLabel={formatPreset.baseAmountLabel}
              lines={calcLines}
              form={form}
              setForm={setForm}
            />
          )}

          <TaxSection form={form} totals={totals} update={update} />
        </div>

        <div className="space-y-3 min-w-0">
          <OpportunitySalesAdjustmentsSection form={form} setForm={setForm} />
          <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              Revision notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              placeholder="What's changing in this revision?"
              className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
            />
          </section>
        </div>

        <div className="space-y-3 min-w-0">
          <DepositSection
            form={form}
            totals={totals}
            update={update}
            depositPreset={formatPreset.depositRule}
          />
        </div>
      </div>

      <aside className="space-y-3 lg:sticky lg:top-4 self-start">
        <OpportunityPricingLiveTotals
          baseAmountLabel={formatPreset.baseAmountLabel}
          baseAmount={inputs.subtotal_input}
          formatEval={formatEval}
          salesAdjEval={salesAdjEval}
          totals={totals}
        />
        {renderSavePanel()}
      </aside>
    </div>
  );
}

function TaxSection(props: {
  form: OpportunityPricingFormState;
  totals: OpportunityPricingTotals;
  update: <K extends keyof OpportunityPricingFormState>(
    key: K,
    value: OpportunityPricingFormState[K],
  ) => void;
}) {
  const { form, totals, update } = props;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Tax</h4>
        <ModeToggle value={form.taxMode} onChange={(m) => update("taxMode", m)} ariaLabel="Tax mode" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          {form.taxMode === "amount" ? (
            <MoneyField label="Tax amount" value={form.taxValue} onChange={(v) => update("taxValue", v)} />
          ) : (
            <PctField
              label="Tax %"
              value={form.taxValue}
              onChange={(v) => update("taxValue", v)}
              defaultPct={null}
              delta={null}
            />
          )}
        </div>
        <ReadOnlyField label="Tax (computed)" value={formatOpportunityCurrency(totals.tax_amount)} compact />
      </div>
    </section>
  );
}

function DepositSection(props: {
  form: OpportunityPricingFormState;
  totals: OpportunityPricingTotals;
  depositPreset: FormatDepositRuleDef;
  update: <K extends keyof OpportunityPricingFormState>(
    key: K,
    value: OpportunityPricingFormState[K],
  ) => void;
}) {
  const { form, totals, depositPreset, update } = props;
  const presetPct =
    depositPreset.deposit_mode === "percent" ? depositPreset.deposit_value : 25;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Deposit</h4>
        <ModeToggle
          value={form.depositMode}
          onChange={(m) => update("depositMode", m)}
          ariaLabel="Deposit mode"
        />
      </div>
      <div className="space-y-3">
        {form.depositMode === "amount" ? (
          <MoneyField
            label="Deposit amount"
            value={form.depositAmountInput}
            onChange={(v) => update("depositAmountInput", v)}
          />
        ) : (
          <PctField
            label="Deposit %"
            value={form.depositPct}
            onChange={(v) => update("depositPct", v)}
            defaultPct={presetPct}
            delta={pctDelta(form.depositPct, presetPct)}
          />
        )}
        <ReadOnlyField
          label="Deposit (computed)"
          value={formatOpportunityCurrency(totals.deposit_amount)}
        />
        <ReadOnlyField
          label="Remaining balance"
          value={formatOpportunityCurrency(totals.remaining_balance)}
        />
        {form.depositMode === "amount" ? (
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
              Deposit deviation note
            </label>
            <textarea
              value={form.depositDeviationNote}
              onChange={(e) => update("depositDeviationNote", e.target.value)}
              rows={2}
              placeholder="Why is the deposit a manual dollar amount?"
              className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ModeToggle(props: {
  value: OpportunityAmountMode;
  onChange: (next: OpportunityAmountMode) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={props.ariaLabel}
      className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs dark:border-zinc-700"
    >
      {(["amount", "percent"] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="radio"
          aria-checked={props.value === mode}
          onClick={() => props.onChange(mode)}
          className={
            "px-2.5 py-1 " +
            (props.value === mode
              ? "bg-indigo-600 text-white"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800")
          }
        >
          {mode === "amount" ? "$" : "%"}
        </button>
      ))}
    </div>
  );
}

function ReadOnlyField(props: { label: string; value: string; compact?: boolean }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">{props.label}</label>
      <div
        className={
          (props.compact ? "mt-1 " : "mt-1 ") +
          "w-full text-sm border border-gray-200 rounded-lg px-3 py-2 tabular-nums bg-gray-50 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
        }
      >
        {props.value}
      </div>
    </div>
  );
}
